import { HubSettingsSource } from '@/services/field.service';

/**
 * The environments a hub can be deployed from: one branch of the Pi's code and
 * one ansible-pull branch that provisions it. They are two independent keys in
 * the hub's Hub.json, but in practice they move together — a hub on
 * `prod-france` pulls `feature/ansible-pull-france` — so the dialog offers the
 * pairs, with either half still free to be typed on its own underneath.
 */
const ENVIRONMENTS = [
    { name: 'dev', pi: 'dev', ansible: 'feature/ansible-pull-dev' },
    { name: 'test', pi: 'test', ansible: 'feature/ansible-pull-test' },
    {
        name: 'montpellier',
        pi: 'prod-montpellier',
        ansible: 'feature/ansible-pull-montpellier',
    },
    {
        name: 'france',
        pi: 'prod-france',
        ansible: 'feature/ansible-pull-france',
    },
    { name: 'world', pi: 'prod-world', ansible: 'feature/ansible-pull-world' },
];

/** What the backend accepts: a branch name, no spaces. */
const BRANCH_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,99}$/;

export interface Branches {
    pi: string | null;
    ansible: string | null;
}

/** The environment both branches belong to, or null for any other pairing. */
function environmentOf({ pi, ansible }: Branches): string | null {
    const env = ENVIRONMENTS.find((e) => e.pi === pi && e.ansible === ansible);
    return env ? env.name : null;
}

/** The branches the hub's Hub.json carries, null for a key it lacks. */
export function branchesOf(hub: HubSettingsSource): Branches {
    return { pi: hub.scanorhizePiBranch, ansible: hub.humeosAnsibleBranch };
}

// An empty field is not a value, it is a field left alone — there is no way
// to clear a branch, since a hub deploys from one or another. So empty is
// valid and simply goes unsent; anything typed has to be a branch name.
export const branchValid = (branch: string) =>
    branch.trim() === '' || BRANCH_RE.test(branch.trim());

/** A typed branch that differs from the hub's, to be written. */
export const branchChanged = (typed: string, current: string | null) =>
    typed.trim() !== '' && typed.trim() !== current;

/**
 * Which branches the hub deploys itself from. These are settings rather than
 * telemetry — saving one rewrites the Hub.json the device reads.
 *
 * A hub whose config carries neither key is still editable: the keys are being
 * rolled out across the fleet, and writing one is how a hub that predates them
 * gets pointed at a branch. Such a field simply starts empty. Saved with the
 * rest of the dialog.
 */
export function BranchFields({
    current,
    pi,
    ansible,
    onChange,
    onEnter,
    onEscape,
}: {
    current: Branches;
    pi: string;
    ansible: string;
    onChange: (branches: { pi: string; ansible: string }) => void;
    onEnter: () => void;
    onEscape: () => void;
}) {
    /** Take an environment: both halves at once. */
    const pick = (name: string) => {
        const env = ENVIRONMENTS.find((e) => e.name === name);
        if (env) onChange({ pi: env.pi, ansible: env.ansible });
    };

    const selected = environmentOf({
        pi: pi.trim() || current.pi,
        ansible: ansible.trim() || current.ansible,
    });

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
                <span className="text-xs text-[#a8b0bb]">Environment</span>
                <div className="flex flex-wrap gap-2">
                    {ENVIRONMENTS.map((env) => (
                        <button
                            key={env.name}
                            type="button"
                            aria-pressed={selected === env.name}
                            onClick={() => pick(env.name)}
                            className={
                                'px-3 py-1 rounded-full text-xs border transition-colors ' +
                                (selected === env.name
                                    ? 'bg-[#1a2620] border-[#74c69d] text-[#74c69d]'
                                    : 'bg-[#0f141a] border-[#2d2d2d] text-[#a8b0bb] hover:text-white hover:border-[#3a4249]')
                            }
                        >
                            {env.name}
                        </button>
                    ))}
                </div>
                <p className="text-[11px] text-[#6b7480]">
                    Sets both branches at once. They can also be typed
                    separately below — the pairs are what we run today, not what
                    is allowed.
                </p>
            </div>

            <BranchField
                id="scanorhize-pi-branch"
                label="scanorhize_pi_branch"
                hint="The branch of the Pi's code this hub runs."
                value={pi}
                known={current.pi !== null}
                valid={branchValid(pi)}
                onChange={(value) => onChange({ pi: value, ansible })}
                onCommit={onEnter}
                onCancel={onEscape}
            />

            <BranchField
                id="humeos-ansible-branch"
                label="humeos_ansible_branch"
                hint="The ansible-pull branch that provisions this hub."
                value={ansible}
                known={current.ansible !== null}
                valid={branchValid(ansible)}
                onChange={(value) => onChange({ pi, ansible: value })}
                onCommit={onEnter}
                onCancel={onEscape}
            />
        </div>
    );
}

/**
 * One branch, as the key it is written under. A hub whose config has no such
 * key yet starts empty and says so: filling it in writes the key for the first
 * time, and leaving it empty changes nothing.
 */
function BranchField({
    id,
    label,
    hint,
    value,
    known,
    valid,
    onChange,
    onCommit,
    onCancel,
}: {
    id: string;
    label: string;
    hint: string;
    value: string;
    /** Whether the hub's Hub.json already carries this key. */
    known: boolean;
    valid: boolean;
    onChange: (value: string) => void;
    onCommit: () => void;
    onCancel: () => void;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-xs text-[#a8b0bb] font-mono">
                {label}
            </label>
            <input
                id={id}
                value={value}
                maxLength={100}
                spellCheck={false}
                placeholder={known ? 'branch name' : 'not in this Hub.json yet'}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') onCommit();
                    else if (event.key === 'Escape') onCancel();
                }}
                className={
                    'px-2 py-1.5 rounded text-sm font-mono bg-[#0f141a] border text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none disabled:opacity-50 ' +
                    (valid
                        ? 'border-[#2d2d2d] focus:border-[#74c69d]'
                        : 'border-[#d9534f]')
                }
            />
            <p
                className={
                    'text-[11px] ' +
                    (valid ? 'text-[#6b7480]' : 'text-[#ff8a84]')
                }
            >
                {!valid
                    ? 'A branch name: letters, digits and . _ - / — no spaces.'
                    : known
                      ? hint
                      : `${hint} This hub's Hub.json has no such key yet — ` +
                        'saving one writes it for the first time.'}
            </p>
        </div>
    );
}
